import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertCircle, Clock, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button.jsx';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  FieldError,
  Input,
  Label,
} from '@/components/ui/primitives.jsx';
import { useAuth } from '@/routes/AuthProvider.jsx';
import { toApiError } from '@/api/client.js';
import { APP_NAME } from '@/lib/constants.js';
import { cn } from '@/lib/utils.js';
import logo from '@/assets/logo.png';

/**
 * Operator sign-in.
 *
 * ## Client validation is a courtesy only
 *
 * The schema below checks shape, not policy. There is deliberately **no
 * minimum-length rule on the password here** even though the API enforces 12
 * characters on a password *change*: an operator whose password predates a
 * policy would be locked out by a client-side rule the server would have
 * accepted, and telling an attacker which of their guesses were long enough to
 * be worth hashing is information for free.
 *
 * ## The three failures that need distinguishing
 *
 * - **401** — wrong email or password. The API deliberately does not say which,
 *   and neither does this.
 * - **423** — the account is locked after five consecutive failures. The
 *   credentials may well be correct; the answer is to wait.
 * - **429** — the *caller* is going too fast, which is a different thing again
 *   and is not per-account.
 *
 * Collapsing these into "login failed" would leave a locked-out operator
 * retrying a correct password for fifteen minutes.
 */

const schema = z.object({
  email: z.string().min(1, 'Email address is required').email('Enter a valid email address'),
  password: z.string().min(1, 'Password is required'),
});

export function LoginPage() {
  const { login, sessionNotice, clearSessionNotice } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [showPassword, setShowPassword] = useState(false);
  const [failure, setFailure] = useState(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { email: '', password: '' },
  });

  const onSubmit = async (values) => {
    setFailure(null);
    clearSessionNotice();

    try {
      await login(values);
      // Back to wherever the operator was headed before their session lapsed.
      navigate(location.state?.from?.pathname ?? '/', { replace: true });
    } catch (error) {
      const apiError = toApiError(error);
      setFailure({
        ...apiError,
        isLocked: apiError.status === 423,
        isRateLimited: apiError.status === 429,
      });
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <img src={logo} alt="" className="mb-3 h-14 w-14" />
          <h1 className="text-lg font-semibold tracking-tight">{APP_NAME}</h1>
        </div>

        {/*
         * Why the last session ended, when it ended by itself. A revocation is
         * called out more strongly than an expiry — it can mean somebody else
         * has the operator's refresh cookie.
         */}
        {sessionNotice ? (
          <div
            className={cn(
              'mb-4 flex items-start gap-2.5 rounded-lg border p-3 text-sm',
              sessionNotice.revoked
                ? 'border-destructive/40 bg-destructive/10'
                : 'border-border bg-muted',
            )}
            role="status"
          >
            <AlertCircle
              className={cn(
                'mt-0.5 h-4 w-4 shrink-0',
                sessionNotice.revoked ? 'text-destructive' : 'text-muted-foreground',
              )}
              aria-hidden="true"
            />
            <div>
              <p>{sessionNotice.message}</p>
              {sessionNotice.revoked ? (
                <p className="mt-1 text-xs text-muted-foreground">
                  This happens when a session token is presented twice. If you did not have two tabs
                  refreshing at once, change your password after signing in.
                </p>
              ) : null}
            </div>
          </div>
        ) : null}

        <Card>
          <CardHeader className="pb-4">
            <CardTitle className="text-base">Sign in</CardTitle>
            <CardDescription>
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" noValidate>
              {failure ? (
                <div
                  className={cn(
                    'flex items-start gap-2.5 rounded-lg border p-3 text-sm',
                    failure.isLocked || failure.isRateLimited
                      ? 'border-warning/40 bg-warning/10'
                      : 'border-destructive/40 bg-destructive/10',
                  )}
                  role="alert"
                >
                  {failure.isLocked || failure.isRateLimited ? (
                    <Clock className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
                  ) : (
                    <AlertCircle
                      className="mt-0.5 h-4 w-4 shrink-0 text-destructive"
                      aria-hidden="true"
                    />
                  )}

                  <div className="min-w-0">
                    <p className="font-medium">{failure.message}</p>

                    {failure.isLocked ? (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Five consecutive failed attempts lock an account for 15 minutes. The lock
                        lifts on its own — a correct password will not clear it early.
                      </p>
                    ) : null}

                    {failure.isRateLimited ? (
                      <p className="mt-1 text-xs text-muted-foreground">
                        Too many sign-in attempts from this address. This is a rate limit on the
                        connection rather than a lock on the account.
                      </p>
                    ) : null}

                    {failure.fields?.length ? (
                      <ul className="mt-1.5 space-y-0.5 text-xs text-muted-foreground">
                        {failure.fields.map((entry, index) => (
                          <li key={index}>{entry.message}</li>
                        ))}
                      </ul>
                    ) : null}
                  </div>
                </div>
              ) : null}

              <div className="space-y-1.5">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="username"
                  autoFocus
                  aria-invalid={Boolean(errors.email)}
                  {...register('email')}
                />
                <FieldError>{errors.email?.message}</FieldError>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    className="pr-9"
                    aria-invalid={Boolean(errors.password)}
                    {...register('password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((current) => !current)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
                <FieldError>{errors.password?.message}</FieldError>
              </div>

              <Button type="submit" className="w-full" loading={isSubmitting}>
                Sign in
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default LoginPage;
